/* eslint-disable no-unused-vars */
// ^ The mirrored body destructures every REWARD_TYPES member for readability,
// including ones no current level happens to use. The server copy isn't
// linted by this project's ESLint, so the disable lives in this header rather
// than in the body — the body must stay byte-identical to the server's.
// MIRROR of server/services/rewards.js — do not edit this file directly.
//
// The SPA is a separate npm project (its own lockfile, its own Node version)
// and cannot import from server/ across that boundary, so this is a verbatim
// copy. tests/rewardsMirror.test.js fails if the two ever differ.
//
// They already drifted once: this copy carried an XP curve of 1000/2000/3000
// per level while the server had 300/550/850, so the profile page showed
// thresholds no player was ever measured against. A comment asking for the
// files to be kept in sync was not enough on its own.

// Define the rewards for each level
export const REWARD_TYPES = Object.freeze({
    Credits: 'credits',
    Gold: 'gold',
    RandomItem: 'random',
    Item: 'item'
})

export const RARITY_TYPES = Object.freeze({
    Common: 'common',
    Rare: 'rare',
    Epic: 'epic',
    Legendary: 'legendary',
    Mythical: 'mythical'
})

const { Credits, Gold, RandomItem, Item } = REWARD_TYPES
const { Common, Rare, Epic, Legendary, Mythical } = RARITY_TYPES

// Gold does not convert to credits anywhere in this codebase.
//
// There used to be an exported GOLD_TO_CREDIT_RATE here, set at 1000. Nothing
// in any runtime path read it — the only importer was a test — so it read like
// an economy rule while being nothing but an accounting figure, and it was free
// to drift away from the shop without anything noticing. It is a comment now so
// it cannot be mistaken for a conversion the game actually performs.
//
// Gold is its own currency. What it is worth is set entirely by the seven
// catalogue items carrying a goldCost (2 to 20 gold each); see
// types/database/data/items.js. Repricing those is how you change gold's value.
//
// For scoring the level track below against its budget, 1 gold is counted as
// 800 credits. That figure exists for THAT ARITHMETIC ONLY. It is not a rate,
// not a promise to any player, and nothing should import or re-export it.

// What a random-item reward pays out in credits when the player already owns
// every item of that tier, so there's nothing left to grant them.
//
// Hardcoded at half the tier's shop price rather than derived from the catalog.
// Deriving it was fragile in both directions: the value silently moved whenever
// an item was repriced or retiered, and a tier holding a single oddly-priced
// entry (Mythical spent a while anchored on a 1-credit joke listing) dragged the
// whole tier with it. Half-price is also the honest number - a random item you
// didn't choose is worth less to you than one you picked off the shelf, and this
// path only fires for a completionist who by definition wanted the item, not the
// credits.
export const TIER_CREDIT_VALUE = Object.freeze({
    [RARITY_TYPES.Common]:    500,
    [RARITY_TYPES.Rare]:      1250,
    [RARITY_TYPES.Epic]:      2500,
    [RARITY_TYPES.Legendary]: 5000,
    [RARITY_TYPES.Mythical]:  10000,
})

/**
 * Credits paid for a random-item reward the player can't receive.
 * @param {string} tier One of RARITY_TYPES.
 * @returns {number} 0 for an unknown tier, so a bad reward definition can't
 *   hand out `undefined` credits and corrupt a balance.
 */
export function getTierValue(tier) {
    // Deliberately not `??`. This file is mirrored verbatim into
    // web/src/assets/js/rewards.js, which is built by webpack 4 — it cannot
    // parse nullish coalescing or optional chaining, and the failure is a
    // build error in the SPA rather than anything visible here. Same reason
    // the rest of this file avoids them.
    const value = TIER_CREDIT_VALUE[tier]
    return typeof value === 'number' ? value : 0
}

// Calibrated to TIME PLAYED, not to an assumed daily routine.
//
// The previous curve (28,000 XP at level 50) was derived from average
// Mixpanel session data - ~375 XP on a typical active day - and so only held
// for someone who played an average amount every day. It said nothing about
// how fast XP could be earned, and XP is paid per second of gameplay with no
// daily cap. Playtesting found the obvious hole: a single sitting reached
// level 5 in 10 minutes, level 17 in an hour, and level 28 in two - because
// the whole 50-level ladder was only ~5.2 hours of play end to end, averaging
// ~6 minutes per level. Daily quests made it worse, since several can complete
// off one game and dump their XP all at once.
//
// This curve is anchored on wall-clock play instead, which can't be shortcut.
// It's deliberately steep rather than linear, on the assumption that players
// grind harder once there's something to grind for - a flat curve just moves
// the exploit later:
//
//   levels 1-5    ~12 min each  (flat, cheap onboarding band)
//   level 10      ~17 min
//   level 20      ~38 min
//   level 30      ~67 min
//   levels 37-50  91-144 min each
//   average       ~61 min per level
//   level 50      182,350 XP total = ~50.6 hours of gameplay
//
// The early band stays cheap so a first session still produces visible
// progress, then the cost climbs hard: the last 14 levels each cost over an
// hour and a half, the final level alone costs 12x the first, and level 50
// costs more than levels 1-9 combined. A player putting in ~26 minutes a day
// alongside their dailies finishes on the last day of a 91-day season.
//
// That top-end wall is also where Gamebot Premium's XP boost is meant to earn
// its keep - it compounds precisely where levels get expensive, and the last
// 14 levels are where a free player feels the climb. That multiplier is live
// as of v3.8.0 - see PREMIUM_XP_MULTIPLIER below - so the shape and the
// mechanism now agree; when this note was written only the shape existed.
//
// Figures above are the base rate of 60 XP/min, no win bonus: winners climb
// ~1.5x faster, subscribers 2x. There was a Season 1 launch boost on top of
// this (1.5x, live for Season 1 only) that made every figure here 1.5x
// faster; it has been reverted and the constant that held it is gone - see
// the note where SEASON_LAUNCH_XP_MULTIPLIER used to be, just above
// PREMIUM_XP_MULTIPLIER below, for what a future boost needs to rebuild.
export const XP_LEVELS = Object.freeze(
    Object.values({
        '1': 700,
        '2': 1400,
        '3': 2100,
        '4': 2800,
        '5': 3500,
        '6': 4250,
        '7': 5050,
        '8': 5900,
        '9': 6850,
        '10': 7850,
        '11': 8950,
        '12': 10150,
        '13': 11450,
        '14': 12900,
        '15': 14450,
        '16': 16150,
        '17': 17950,
        '18': 19900,
        '19': 22000,
        '20': 24250,
        '21': 26650,
        '22': 29200,
        '23': 31900,
        '24': 34800,
        '25': 37850,
        '26': 41100,
        '27': 44550,
        '28': 48150,
        '29': 51950,
        '30': 55950,
        '31': 60150,
        '32': 64550,
        '33': 69150,
        '34': 73950,
        '35': 79000,
        '36': 84250,
        '37': 89700,
        '38': 95400,
        '39': 101300,
        '40': 107450,
        '41': 113850,
        '42': 120450,
        '43': 127300,
        '44': 134400,
        '45': 141750,
        '46': 149350,
        '47': 157200,
        '48': 165300,
        '49': 173700,
        '50': 182350
    })
)


// There used to be a SEASON_LAUNCH_XP_MULTIPLIER here: 1.5x for Season 1, to
// drive engagement with the leveling feature while it was unfamiliar. It was
// reverted to 1 first, then removed outright once nothing needed to read it -
// with the boost over, keeping a constant permanently equal to 1 was one more
// thing to understand for no behavior it was providing.
//
// Removing it made the whole curve 1.5x slower: level 50 went from ~34 hours
// of gameplay to ~50.6. That was the point of the revert. At 1.5x a free
// player cleared the entire 50-level track on ~22 minutes a day, which is not
// a season.
//
// It never expired on its own - season rollover doesn't touch anything like
// this (see server/services/seasons.js) - so the revert was manual, and nothing
// here does that automatically. A future launch boost needs three things this
// one had: an exported multiplier here, xpMultiplier() in RewardsManager.js
// multiplying it back in, and a plan for turning it off that isn't "someone
// remembers." The last part is what actually went missing last time.
//
/**
 * Gamebot Premium's XP multiplier. Applied on top of any launch boost, so a
 * subscriber always earns exactly twice what the same game would pay a free
 * player, whatever the base rate happens to be.
 *
 * This is the "Double XP" the sales page promises. If you change it, change
 * the copy in web/src/globals/premium.js with it — tests/premiumXp.test.js
 * fails if the two disagree.
 */
export const PREMIUM_XP_MULTIPLIER = 2

// Free tier: rewards on 20 of the 50 levels (premium: false), claimable by every user.
// Premium tier: rewards on the other 30 (premium: true), claimable only by premium
// subscribers - these are the "extra" levels that make leveling feel continuous for Plus
// users. Every level from 1 to 50 pays somebody; no level is ever blank.
//
// This comment used to say the free tier paid "every 5 levels". It never did - the table
// below has always paid free rewards on 20 levels, roughly every 2.5. The claim survived
// long enough to be quoted in a planning discussion and cost real time, so: the level
// counts here are load-bearing, and tests/rewardTrackShape.test.js asserts them.
//
// Season resets 4x/year, so these totals repeat 4x annually: 16,100 credits-equivalent per
// season to a free player who reaches 50, ~64,400 a year.
//
// Credit grants are all a round 500 or 1,000. Earlier values (280/410/530/...) were
// arithmetically tidy but read as noise; players reason in round numbers, and the shop is
// priced in thousands (common 1,000 / rare 2,500 / epic 5,000 / legendary 10,000). Nothing
// pays less than 500 - a grant beneath that is under half the cheapest thing in the shop
// and reads as a rounding error rather than a reward.
//
// Each level maps to an ARRAY of rewards (usually one). Level 50 has two: the free-tier
// finale, plus a premium-only bonus random Mythical item (or 10,000 credits via
// getTierValue() if the user already owns every Mythical). Keyed by level number directly
// (not array-indexed) so getReward(level) can't drift off-by-one the way an
// Object.values()-derived array would.
/*
 * The reward track.
 *
 * ## Shape
 *
 *   20 item grants   (5 common, 9 rare, 4 epic, 1 legendary, 1 mythical)
 *    8 gold grants
 *   23 credit grants (all 500 or 1,000)
 *   ─────────────────
 *   51 grants over 50 levels, every one of which pays something. Level 50 is
 *   the one that pays twice, as it did before — see the note on it below.
 *
 * ## Budget: items are worth less than they cost to replace
 *
 * A random-item reward isn't worth its shop price to the player who gets it —
 * most of the time it's a tier they didn't pick, not the one they wanted. For
 * BUDGETING the track (deciding how many draws it can afford), items are
 * valued at 40% of shop price:
 *
 *   common 400, rare 900, epic 2,000, legendary 4,000, mythical 8,000
 *
 * That is a different number from TIER_CREDIT_VALUE (50% of shop price)
 * on purpose, and the two must not be confused for each other:
 *
 *   - TIER_CREDIT_VALUE is a real payout — what the exhausted-tier fallback
 *     in server/routes/rewards.js actually pays when a player already owns
 *     every item of a tier. That path fires for a completionist who by
 *     definition wanted the item, so it pays closer to full value.
 *   - The 40% figure above is not exported and pays nobody anything. It
 *     exists only so this comment (and tests/rewardTrackShape.test.js, which
 *     keeps its own copy) can reason about what the track costs to fund, the
 *     same role 800 plays for gold above.
 *
 * Rating a draw below its shop price is what lets item grants scale without
 * eating credit-grant budget one-for-one — items got measurably more common
 * below (20 grants, up from the previous track's 21… but see Pool depth: the
 * mix moved off Common into Rare/Epic, which is the part that reads as an
 * upgrade) without shrinking anyone's credits.
 *
 *   items    30,100  (at the 40% budgeting value above)
 *   gold      6,400  (8 x 1 gold at 800)
 *   credits  19,500
 *   ─────────────────
 *   total    56,000
 *
 *   free     16,000   1 common, 5 rare, 1 epic, 2 gold, 7,500 credits
 *   premium  40,000   4 common, 4 rare, 3 epic, legendary, mythical,
 *                     6 gold, 12,000 credits
 *
 * Both land on a round number exactly.
 *
 * ## Where the free value sits, and why it isn't further forward
 *
 * Mixpanel says almost nobody finishes. Two weeks after the reward track
 * shipped, of 3,267 players who levelled at all: 27.5% passed level 10, 5.7%
 * passed 20, 0.9% passed 30, and ZERO reached 50. Median play is ~2 min/day;
 * even the 90th percentile plays ~9 min/day, which over a full 91-day season
 * lands them around level 27. The season is young and that will drift upward,
 * but the shape is clear: value parked above level 40 is mostly undelivered.
 *
 * That argues for moving value forward. Pulling too hard the other way is
 * worse, though, because the curve is steep: L1-20 is ~7.4 hours of play,
 * L36-50 is ~31.5. Free value per hour of play, at the ~3,278 XP/hr the
 * telemetry actually shows:
 *
 *   L1-20    5,100 over  7.4h = 689/hr
 *   L21-35   4,400 over 16.7h = 263/hr
 *   L36-50   6,500 over 31.5h = 206/hr
 *
 * Already a 3x slide from start to finish, because the curve steepens faster
 * than the rewards do. An earlier draft of this pass moved credits forward as
 * well as items and pushed the last band to 162/hr — a 5x slide, paying least
 * exactly where the grind is worst. That was reverted.
 *
 * What stayed is items-only: levels 11 and 18 went Common -> Rare, funded by
 * trimming 500 each from levels 20 and 22. Early density rises 622 -> 689/hr,
 * the last band is untouched at 206/hr, and a player in the first 20 levels —
 * where nearly everyone actually is — now sees three Rare drops instead of
 * one. Item TIER moved forward; credit WEIGHT stayed back.
 *
 * The real fix is not reward placement, it is that level 50 costs 12x level 1.
 * While that holds, any distribution has to choose between an unrewarding
 * late game and unreachable rewards. Flattening the back half of XP_LEVELS is
 * the change that would dissolve the trade; this table only balances it.
 *
 * The free Epic at level 50 stays put. It is the one item on the free track
 * worth showing anyone, and a finale nobody has reached yet is still a finale
 * — see the note on level 50 below.
 *
 * The premium:free ratio holds at 2.50x. Differentiation is tier, not volume:
 * premium holds the only Legendary, the only Mythical, three of the four
 * Epics, and exactly twice the gold and the credits.
 *
 * ## Pool depth
 *
 * 5 commons against 25 in the catalogue, 9 rares against 23, 4 epics against
 * 17, 1 legendary against 5. Rare is the busiest tier now that the free track
 * leans on it, and still draws against barely a third of its pool. Mythical is
 * the tight one at 2 items: a player who already owns both gets 10,000 credits
 * instead, via the exhausted-tier path in server/routes/rewards.js — at
 * TIER_CREDIT_VALUE (50%), not the 40% this track was budgeted against, which
 * is why that path pays out ahead of what funding it assumed. See Budget.
 *
 * Legendary and mythical are premium-only, so the two scarcest pools are
 * never drawn against by the free track.
 */
export const LEVEL_REWARDS = Object.freeze({
    "1":  [{ "type": Credits,    "premium": false, "value": 500 }],
    "2":  [{ "type": Credits,    "premium": true,  "value": 1000 }],
    "3":  [{ "type": RandomItem, "premium": true,  "value": Common }],
    "4":  [{ "type": RandomItem, "premium": false, "value": Common }],
    "5":  [{ "type": Credits,    "premium": true,  "value": 1000 }],
    "6":  [{ "type": Gold,       "premium": true,  "value": 1 }],
    "7":  [{ "type": Credits,    "premium": true,  "value": 1000 }],
    "8":  [{ "type": RandomItem, "premium": false, "value": Rare }],
    "9":  [{ "type": RandomItem, "premium": true,  "value": Common }],
    "10": [{ "type": Credits,    "premium": true,  "value": 1000 }],
    "11": [{ "type": RandomItem, "premium": false, "value": Rare }],
    "12": [{ "type": Credits,    "premium": false, "value": 500 }],
    "13": [{ "type": RandomItem, "premium": true,  "value": Common }],
    "14": [{ "type": Credits,    "premium": true,  "value": 1000 }],
    "15": [{ "type": Credits,    "premium": false, "value": 500 }],
    "16": [{ "type": Gold,       "premium": true,  "value": 1 }],
    "17": [{ "type": Credits,    "premium": true,  "value": 1000 }],
    "18": [{ "type": RandomItem, "premium": false, "value": Rare }],
    "19": [{ "type": RandomItem, "premium": true,  "value": Common }],
    "20": [{ "type": Credits,    "premium": false, "value": 500 }],
    "21": [{ "type": Credits,    "premium": true,  "value": 1000 }],
    "22": [{ "type": Credits,    "premium": false, "value": 500 }],
    "23": [{ "type": Gold,       "premium": true,  "value": 1 }],
    "24": [{ "type": RandomItem, "premium": true,  "value": Rare }],
    "25": [{ "type": Credits,    "premium": false, "value": 500 }],
    "26": [{ "type": Credits,    "premium": true,  "value": 1000 }],
    "27": [{ "type": Gold,       "premium": true,  "value": 1 }],
    "28": [{ "type": Credits,    "premium": false, "value": 1000 }],
    "29": [{ "type": Gold,       "premium": true,  "value": 1 }],
    "30": [{ "type": Credits,    "premium": false, "value": 1000 }],
    "31": [{ "type": RandomItem, "premium": true,  "value": Rare }],
    "32": [{ "type": Credits,    "premium": true,  "value": 1000 }],
    "33": [{ "type": RandomItem, "premium": false, "value": Rare }],
    "34": [{ "type": RandomItem, "premium": true,  "value": Epic }],
    "35": [{ "type": Credits,    "premium": false, "value": 500 }],
    "36": [{ "type": Gold,       "premium": true,  "value": 1 }],
    "37": [{ "type": RandomItem, "premium": true,  "value": Rare }],
    "38": [{ "type": Gold,       "premium": false, "value": 1 }],
    "39": [{ "type": Credits,    "premium": true,  "value": 1000 }],
    "40": [{ "type": Credits,    "premium": false, "value": 1000 }],
    "41": [{ "type": RandomItem, "premium": true,  "value": Epic }],
    "42": [{ "type": RandomItem, "premium": true,  "value": Rare }],
    "43": [{ "type": RandomItem, "premium": false, "value": Rare }],
    "44": [{ "type": RandomItem, "premium": true,  "value": Legendary }],
    "45": [{ "type": Credits,    "premium": false, "value": 1000 }],
    "46": [{ "type": RandomItem, "premium": true,  "value": Epic }],
    "47": [{ "type": Gold,       "premium": false, "value": 1 }],
    "48": [{ "type": Credits,    "premium": true,  "value": 1000 }],
    "49": [{ "type": Credits,    "premium": true,  "value": 1000 }],
    // The only level that pays both, as the last one did before. A free
    // player reaching 50 has put in something like 50 hours; handing them
    // nothing at the top of that climb because the finale is a premium
    // mythical is not a trade the free track should be asked to make.
    //
    // The free half is an Epic rather than a credit payout - the one Epic on
    // the free track, and the only item a free player gets all season that is
    // worth showing anyone. It is what the climb is for.
    "50": [
        { "type": RandomItem, "premium": false, "value": Epic },
        { "type": RandomItem, "premium": true,  "value": Mythical }
    ]
})

// Getter function to retrieve the rewards array for a specific level.
export function getReward(level) {
  return LEVEL_REWARDS[level] || [];
}

/**
 * The levels this user could claim something from right now.
 *
 * "Could claim" is the whole point — it drives a message that says *claim
 * them*, so counting anything the user cannot actually take makes that
 * message a lie they can't act on.
 *
 * Claims are recorded in two arrays, and a level lands in `rewardsClaimed`
 * only if it paid something non-premium. 39 of the 50 levels are
 * premium-only, so they never appear there at all — an earlier version of
 * this checked `rewardsClaimed` alone and therefore reported those levels as
 * unclaimed forever. A free player who had taken everything available to
 * them was still told to go and claim their rewards.
 *
 * A premium-locked reward is not counted for a non-subscriber. It is a real
 * backlog, but it belongs to the upsell — "N rewards are waiting behind
 * Gamebot Premium" — rather than to a claim prompt. The two counts are
 * complementary now instead of overlapping.
 *
 * Mirrors partitionRewards() in server/routes/rewards.js, which is what
 * actually decides a claim; this is the read-only question of whether that
 * would find anything.
 *
 * Pure and side-effect free: takes a user document, returns an array.
 *
 * @param {object} user A user document (`level`, `rewardsClaimed`,
 *   `premiumRewardsClaimed`, `premium`).
 * @returns {number[]} Ascending level numbers with something claimable.
 */
export function unclaimedLevels(user) {
  const level = (user && user.level) || 0;
  const claimedFree = (user && user.rewardsClaimed) || [];
  const claimedPremium = (user && user.premiumRewardsClaimed) || [];
  const isPremium = user && user.premium === true;

  const levels = [];
  for (let lvl = 1; lvl <= level; lvl++) {
    const hasClaimable = getReward(lvl).some(reward => {
      const already = reward.premium
        ? claimedPremium.includes(lvl)
        : claimedFree.includes(lvl);
      if (already) return false;
      // Locked rather than unclaimed: the upsell's business, not a claim's.
      return !reward.premium || isPremium;
    });
    if (hasClaimable) levels.push(lvl);
  }
  return levels;
}

/**
 * The other half of that count: levels holding a reward this user has
 * already earned and cannot take, because it is premium and they are not.
 *
 * The complement unclaimedLevels() describes but deliberately doesn't
 * return. Together the two partition a player's earned-but-unclaimed
 * backlog into "go and claim this" and "this is what a subscription would
 * hand you", and nothing appears in both.
 *
 * Empty for a subscriber, always — for them nothing is locked, and whatever
 * they have not claimed is already counted by unclaimedLevels(). That is what
 * makes this safe to call unconditionally at the end of a game: the upsell
 * disappears for the people who bought it without the caller having to ask a
 * second question.
 *
 * Pure and side-effect free, same as its neighbour above.
 *
 * @param {object} user A user document.
 * @returns {number[]} Ascending level numbers with a locked premium reward.
 */
export function lockedLevels(user) {
  if (!user || user.premium === true) return [];

  const level = user.level || 0;
  const claimedPremium = user.premiumRewardsClaimed || [];

  const levels = [];
  for (let lvl = 1; lvl <= level; lvl++) {
    // `claimedPremium` can legitimately hold levels for somebody who is not
    // a subscriber now — they claimed while they were one, and lapsing never
    // claws back what was already taken (see DatabaseClient#setPremiumStatus).
    const locked = getReward(lvl).some(reward => reward.premium && !claimedPremium.includes(lvl));
    if (locked) levels.push(lvl);
  }
  return levels;
}
